import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi } from '../lib/api';

/**
 * Auth Store con Zustand
 * Gestisce autenticazione, utente corrente, tenant e ruoli
 */
const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      tenant: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      /**
       * Login
       */
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.login(email, password);
          const { user, tenant, accessToken } = response.data;

          // Salva token in localStorage (per l'interceptor)
          localStorage.setItem('accessToken', accessToken);

          set({
            user,
            tenant,
            accessToken,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });

          return { success: true, user };
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Errore durante il login';
          set({
            isLoading: false,
            error: errorMessage,
            isAuthenticated: false
          });
          return { success: false, error: errorMessage };
        }
      },

      /**
       * Registrazione
       */
      register: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await authApi.register(data);
          const { user, tenant, accessToken } = response.data;

          localStorage.setItem('accessToken', accessToken);

          set({
            user,
            tenant,
            accessToken,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });

          return { success: true, user };
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Errore durante la registrazione';
          set({
            isLoading: false,
            error: errorMessage
          });
          return { success: false, error: errorMessage };
        }
      },

      /**
       * Logout
       */
      logout: async () => {
        try {
          await authApi.logout();
        } catch (error) {
          console.error('Errore logout:', error);
        } finally {
          localStorage.removeItem('accessToken');
          set({
            user: null,
            tenant: null,
            accessToken: null,
            isAuthenticated: false,
            error: null
          });
        }
      },

      /**
       * Logout da tutti i device
       */
      logoutAll: async () => {
        try {
          await authApi.logoutAll();
        } catch (error) {
          console.error('Errore logout all:', error);
        } finally {
          localStorage.removeItem('accessToken');
          set({
            user: null,
            tenant: null,
            accessToken: null,
            isAuthenticated: false,
            error: null
          });
        }
      },

      /**
       * Fetch profilo utente
       * Usato per inizializzare lo stato all'avvio
       */
      fetchMe: async () => {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          set({ isAuthenticated: false });
          return;
        }

        set({ isLoading: true });
        try {
          const response = await authApi.getMe();
          const { user, tenant } = response.data;

          set({
            user,
            tenant,
            accessToken: token,
            isAuthenticated: true,
            isLoading: false,
            error: null
          });
        } catch (error) {
          console.error('Errore fetch profilo:', error);
          localStorage.removeItem('accessToken');
          set({
            user: null,
            tenant: null,
            accessToken: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      },

      /**
       * Cambio password
       */
      changePassword: async (currentPassword, newPassword) => {
        set({ isLoading: true, error: null });
        try {
          await authApi.changePassword(currentPassword, newPassword);
          set({ isLoading: false, error: null });
          return { success: true };
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'Errore durante il cambio password';
          set({ isLoading: false, error: errorMessage });
          return { success: false, error: errorMessage };
        }
      },

      /**
       * Helper: verifica se l'utente ha un certo ruolo
       */
      hasRole: (role) => {
        const { user } = get();
        if (!user) return false;
        if (Array.isArray(role)) {
          return role.includes(user.role);
        }
        return user.role === role;
      },

      /**
       * Helper: verifica se è superadmin
       */
      isSuperAdmin: () => {
        const { user } = get();
        return user?.role === 'superadmin';
      },

      /**
       * Helper: verifica se è admin del tenant
       */
      isAdmin: () => {
        const { user } = get();
        return user?.role === 'admin' || user?.role === 'superadmin';
      },

      /**
       * Helper: verifica se può editare
       */
      canEdit: () => {
        const { user } = get();
        return ['admin', 'editor', 'superadmin'].includes(user?.role);
      },

      /**
       * Clear error
       */
      clearError: () => set({ error: null })
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // Persisti solo questi campi
        user: state.user,
        tenant: state.tenant,
        isAuthenticated: state.isAuthenticated
        // NON persistere accessToken (sta in localStorage separato)
      })
    }
  )
);

export default useAuthStore;
